import React, { useState, useContext } from "react";
import moment from "moment-timezone";
import { Modal, Loading } from "carbon-components-react";

import { formatPhoneNumber } from "@eave/shared/utils/phone";
import {
  HLHLContact,
  HLHLContactField,
  HLHLContactFieldDisplayValues,
  HLHLContactRoleDisplayValues,
} from "@eave/shared/types/api";

import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import { LoadingState } from "underwriter_dashboard/shared/types";
import { TableHeaderDataShape } from "web_modules/shared/BaseTable/types";
import { isUserAdmin } from "web_modules/shared/session";

import AddContactForm from "./AddContactForm";
import { HLHLContactsContext } from "./HLHLContactsProvider";
import { initialValues } from "./FormValues";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

const hlhlContactsHeaderData: TableHeaderDataShape[] = [
  {
    header: HLHLContactFieldDisplayValues[HLHLContactField.FullName],
    key: HLHLContactField.FullName,
  },
  {
    header: HLHLContactFieldDisplayValues[HLHLContactField.Email],
    key: HLHLContactField.Email,
  },
  {
    header: HLHLContactFieldDisplayValues[HLHLContactField.Role],
    key: HLHLContactField.Role,
  },
  {
    header: HLHLContactFieldDisplayValues[HLHLContactField.Phone],
    key: HLHLContactField.Phone,
  },
  {
    header: HLHLContactFieldDisplayValues[HLHLContactField.DefaultSlackNotify],
    key: HLHLContactField.DefaultSlackNotify,
  },
  {
    header: HLHLContactFieldDisplayValues[HLHLContactField.UpdatedAt],
    key: HLHLContactField.UpdatedAt,
  },
];

const tz = moment.tz.guess();
const formatTime = (t: string): string => moment(t).tz(tz).format("MM/DD/YY h:mma z");

const formatName = (
  firstName: string = "",
  middleName: string = "",
  lastName: string = "",
  suffix: string = "",
): string => {
  let name: string;
  if (middleName) {
    name = `${firstName} ${middleName} ${lastName}`;
  } else {
    name = `${firstName} ${lastName}`;
  }
  return suffix ? `${name}, ${suffix}` : name;
};

export const formatPhone = (phone: string | null): string => {
  if (!phone) {
    return phone;
  }
  const rawPhone = phone.replace(/-|\(|\)| /gi, "");
  return `${rawPhone.substring(0, 3)}-${rawPhone.substring(3, 6)}-${rawPhone.substr(6, 11)}`;
};

const renderHLHLContactRow = (contact: HLHLContact) => {
  const {
    id,
    email,
    role,
    phone,
    updated_at,
    first_name,
    last_name,
    middle_name,
    suffix,
    default_slack_notify,
  } = contact;

  return {
    id: id.toString(),
    full_name: formatName(first_name, middle_name, last_name, suffix),
    email,
    role: HLHLContactRoleDisplayValues[role],
    phone: phone && formatPhoneNumber(phone),
    updated_at: formatTime(updated_at),
    default_slack_notify: default_slack_notify ? "Yes" : "No",
  };
};

const HLHLContacts: React.FC<any> = () => {
  const [addContactModalIsOpen, setAddContactModalIsOpen] = useState(false);
  const [deactivateContactsModalIsOpen, setDeactivateContactsModalIsOpen] = useState(false);
  const [activateContactsModalIsOpen, setActivateContactsModalIsOpen] = useState(false);
  const [editingMode, updateEditingMode] = useState(EditingState.CREATE);
  const [formState, updateFormState] = useState(initialValues);

  const { hlhlContacts, saveContact, loading } = useContext(HLHLContactsContext);

  const activeContacts = hlhlContacts.filter(c => c.active);
  const inactiveContacts = hlhlContacts.filter(c => !c.active);

  return (
    <div id="hlhl-contacts">
      <BaseTable
        key="hlhl-contacts-active"
        addNewButtonID="addNewHLHLContact"
        title="Currently active"
        deleteActionText="Deactivate"
        disableBatchEdit
        allowHeaderTextWrapping
        disableBatchDelete
        disableBatch
        disableAllControls={!isUserAdmin()}
        rowHeight="short"
        useZebraStyles
        openModal={() => setAddContactModalIsOpen(true)}
        openDeleteModal={() => setDeactivateContactsModalIsOpen(true)}
        updateFormState={data => updateFormState(data)}
        updateEditingMode={updateEditingMode}
        headerData={hlhlContactsHeaderData}
        rawRowData={activeContacts}
        rowData={activeContacts.map(renderHLHLContactRow)}
      />
      <BaseTable
        key="hlhl-contacts-inactive"
        title="Inactive"
        deleteActionText="Activate"
        disableBatchEdit
        disableAllControls={!isUserAdmin()}
        allowHeaderTextWrapping
        disableBatchDelete
        disableBatch
        rowHeight="short"
        useZebraStyles
        disableAddNew
        openModal={() => setAddContactModalIsOpen(true)}
        openDeleteModal={() => setActivateContactsModalIsOpen(true)}
        updateFormState={data => updateFormState(data)}
        updateEditingMode={updateEditingMode}
        headerData={hlhlContactsHeaderData}
        rawRowData={inactiveContacts}
        rowData={inactiveContacts.map(renderHLHLContactRow)}
        collapseOverride
      />
      <Modal
        className={`${styles.patchModalBottomPaddingIssue} ${styles.patchModalTooltipZIndex}`}
        passiveModal
        open={addContactModalIsOpen}
        modalHeading={editingMode === EditingState.CREATE ? "Add contact" : "Update contact"}
        selectorPrimaryFocus="#role"
        onRequestClose={() => {
          setAddContactModalIsOpen(false);
          updateFormState(initialValues);
        }}
        onBlur={() => {
          setAddContactModalIsOpen(false);
          updateFormState(initialValues);
        }}
      >
        <AddContactForm
          formState={formState}
          onSubmit={contact => {
            return saveContact(contact).then(() => {
              setAddContactModalIsOpen(false);
              updateFormState(initialValues);
            });
          }}
          editingMode={editingMode}
          onRequestClose={() => {
            setAddContactModalIsOpen(false);
            updateFormState(initialValues);
          }}
          loading={loading}
        />
      </Modal>
      <Modal
        className={styles.patchModalBottomPaddingIssue}
        danger
        open={deactivateContactsModalIsOpen}
        modalHeading="Are you sure you'd like to deactivate this contact?"
        primaryButtonText="Deactivate"
        secondaryButtonText="Cancel"
        onBlur={() => setDeactivateContactsModalIsOpen(false)}
        onRequestClose={() => setDeactivateContactsModalIsOpen(false)}
        onRequestSubmit={() => {
          saveContact({
            ...formState,
            active: false,
            phone: formatPhone(formState.phone),
          } as HLHLContact);
          setDeactivateContactsModalIsOpen(false);
          updateFormState(initialValues);
        }}
      >
        <Loading active={loading === LoadingState.ACTIVE} />
      </Modal>
      <Modal
        className={styles.patchModalBottomPaddingIssue}
        danger
        open={activateContactsModalIsOpen}
        modalHeading="Are you sure you'd like to activate this contact?"
        primaryButtonText="Activate"
        secondaryButtonText="Cancel"
        onBlur={() => setActivateContactsModalIsOpen(false)}
        onRequestClose={() => setActivateContactsModalIsOpen(false)}
        onRequestSubmit={() => {
          saveContact({
            ...formState,
            active: true,
            phone: formatPhone(formState.phone),
          } as HLHLContact);
          setActivateContactsModalIsOpen(false);
          updateFormState(initialValues);
        }}
      >
        <Loading active={loading === LoadingState.ACTIVE} />
      </Modal>
    </div>
  );
};

export default HLHLContacts;
